import { RichText, RichTextField } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'components/helpers/Button';
import NrcMenuClose from 'components/authorable/site/Header/SvgMenuClose/nrc-menu-close';
import { signIn, useSession } from 'next-auth/react';
import useDictionary from 'lib/sitecore/use-dictionary';
import { pageNavigationDataLayerPush, menuLocation } from 'lib/util/gtm-datalayer';

const Anonymous = ({ ...profileItem }): JSX.Element => {
  const [utilityNavOpen, setUtilityNavOpen] = useState(false);
  const utilityNavRef = useRef<HTMLDivElement | null>(null); // Create a ref for the UtilityNav element.
  const { status } = useSession();
  const dictionary = useDictionary();
  const fields = profileItem?.profileItem.profileItem;

  // Define the closeUtilityNav function inside a useCallback hook.
  const closeUtilityNav = useCallback(() => {
    setUtilityNavOpen(false);
  }, []);

  // Use the useEffect hook to listen for clicks on the document.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click occurred outside of the UtilityNav component.
      if (utilityNavRef.current && !utilityNavRef.current.contains(event.target as Node)) {
        closeUtilityNav(); // Close the utility nav.
      }
    };

    // Add an event listener when the component mounts.
    document.addEventListener('click', handleClickOutside);

    // Remove the event listener when the component unmounts.
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [closeUtilityNav, utilityNavRef]);
  const toggleUtilityNav = () => {
    setUtilityNavOpen((prev) => !prev);
    pageNavigationDataLayerPush(fields?.createAccountLinkText?.value, menuLocation[0]);
  };

  const signInRedirect = () => {
    pageNavigationDataLayerPush(dictionary.Login, menuLocation[0]);

    if (status !== 'authenticated') {
      signIn('okta');
    }
  };

  const cssItemsCenter = 'items-center';
  const cssTextBlueNavy = 'text-blue-navy';
  const cssFontMedium = 'font-medium';
  const cssXlBorder = 'xl:border-0';

  return (
    <>
      <div ref={utilityNavRef}>
        <ul
          role="menu"
          className={classnames(
            'border-t-[24px]',
            'border-gray-whisper',
            cssItemsCenter,
            cssTextBlueNavy,
            cssFontMedium,
            'flex',
            'flex-col-reverse',
            cssXlBorder,
            'xl:flex-row',
            'xl:relative',
            'xl:top-auto'
          )}
        >
          <li
            role="menuitem"
            className={classnames(
              'flex',
              'w-full',
              'xl:mr-8',
              'font-bold',
              'text-blue',
              'relative',
              'justify-center'
            )}
          >
            <button
              type="button"
              onClick={toggleUtilityNav}
              className={classnames('z-20', 'font-bold', 'w-full')}
            >
              {fields?.createAccountLinkText?.value}
            </button>
            <div
              className={classnames(
                'create-account-dropdown',
                'p-4',
                'w-[90%]',
                'h-fit',
                'text-sm',
                'rounded-t-2xl',
                'bottom-[35px]',
                'border-b-[8px]',
                'sm:max-w-[395px]',
                'xl:right-[-100%]',
                'xl:min-w-[395px]',

                {
                  hidden: !utilityNavOpen,
                  block: utilityNavOpen,
                }
              )}
            >
              <button
                type="button"
                onClick={() => setUtilityNavOpen(false)}
                className={classnames('inline-flex', 'h-4', 'w-4', 'float-right', 'xl:hidden')}
                aria-label="Close Menu"
              >
                <NrcMenuClose className={classnames('mx-auto')} />
              </button>
              <RichText field={fields?.createAccountMessage as RichTextField} />
            </div>
          </li>
          <li
            role="menuitem"
            className={classnames('w-full', 'my-6', 'xl:my-0', 'px-4', 'xl:w-auto')}
            aria-label={dictionary.Login}
          >
            <Button
              className={classnames('w-full', 'text-center')}
              text={dictionary.Login}
              variant="primary"
              onClick={signInRedirect}
            />
          </li>
        </ul>
      </div>
    </>
  );
};

export default Anonymous;
